import axios, { AxiosResponse, CancelTokenSource } from 'axios'
import { format } from 'date-fns'

import { ProgressSummary } from '../components/views/progress/components/ProgressCharts/ProgressCharts'

import { Item } from './items'

const BASE_URL = process.env.REACT_APP_NEXTERA_API

export const listTrackerJobTypes = (
  projectId: string,
  tracker: string,
): Promise<AxiosResponse<ProjectJobType[]>> => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/tracker/${tracker}/job-types`)
    .then(r => r.data)
}

export const listJobsForProjectTrackerFloor = async (
  projectId: string,
  activeTracker: string,
  floorCode: string,
  observationDate?: Date | null,
  cancelTokenSource?: CancelTokenSource,
  includeStlUrl = false,
  review = false,
): Promise<Job[]> => {
  const stlParam = includeStlUrl ? '&include_stl_url=1' : ''
  const reviewParam = review ? '&review=1' : ''
  const res = await axios.get(
    `${BASE_URL}/project/${projectId}/tracker/${activeTracker}/jobs?observation_date=${
      observationDate ? format(observationDate, 'yyyy-MM-dd') : ''
    }&floor=${floorCode}${stlParam}${reviewParam}`,
    {
      cancelToken: cancelTokenSource?.token,
    },
  )

  const response = await res.data
  const rows = response.data || []
  return review ? rows.map(reviewJobToJob) : rows
}

export const getJobsByViewpoint = (
  projectId: string,
  tracker: string,
  viewpoint: number,
  observationDate?: Date | null,
  includeStlUrl = false,
  cancelTokenSource?: CancelTokenSource,
  review = false,
) => {
  return axios
    .get(`${BASE_URL}/project/${projectId}/tracker/${tracker}/jobs`, {
      params: {
        viewpoint: viewpoint,
        observation_date: observationDate
          ? format(observationDate, 'yyyy-MM-dd')
          : '',
        ...(includeStlUrl ? { include_stl_url: 1 } : {}),
        ...(review ? { review: 1 } : {}),
      },
      cancelToken: cancelTokenSource?.token,
    })
    .then(r => r.data)
    .then(body =>
      review
        ? { ...body, data: (body.data || []).map(reviewJobToJob) }
        : body,
    )
}

export const getProgressSummaryByDate = async (
  projectId: string,
  tracker: string,
  floor_code: string,
): Promise<ProgressSummary> => {
  const res = await axios.get(
    `${BASE_URL}/project/${projectId}/tracker/${tracker}/jobs/summary?floor=${floor_code}`,
  )
  const response = await res.data

  return response.data
}

/** Slim row from `GET .../jobs?review=1`. Mapped onto `Job` for the review UI. */
interface ReviewJobRow {
  id: number
  total_units: number
  completed_units: number
  is_active: boolean
  project_job_type_id: number
  item_id: number
  project_tracker: number
  viewpoint: number
  floor_code: string | null
  job_type: {
    id: number
    name: string
    slug: string
    display_color: string
    secondary_display_color: string | null
    tertiary_display_color: string | null
    display_shape: string
  }
  position: { x: number; y: number; order: number }[]
}

const reviewJobToJob = (row: ReviewJobRow): Job => ({
  id: row.id,
  completed_units: row.completed_units,
  total_units: row.total_units,
  status: '',
  rate: 0,
  registered_on: '',
  last_modified_on: '',
  initial_observation_date: null,
  type: {
    id: row.project_job_type_id,
    is_active: row.is_active,
    project: 0,
    job_type: {
      id: row.job_type.id,
      name: row.job_type.name,
      slug: row.job_type.slug,
      units: '',
      display_color: row.job_type.display_color,
      secondary_display_color: row.job_type.secondary_display_color,
      tertiary_display_color: row.job_type.tertiary_display_color,
      display_shape: row.job_type.display_shape,
      display_order: 0,
      item_type: 0,
    },
  },
  item: {
    id: row.item_id,
    type: { name: '' },
    position: row.position || [],
    registered_on: '',
    last_modified_on: '',
    project_tracker: row.project_tracker,
    viewpoint: row.viewpoint,
    floor_code: row.floor_code,
  },
})

export interface Job {
  id: number
  type: ProjectJobType
  item: Item
  completed_units: number
  registered_on: string
  last_modified_on: string
  status: string
  total_units: number
  rate: number
  initial_observation_date: string | null
}

export interface JobType {
  id: number
  name: string
  slug: string
  units: string
  display_color: string
  secondary_display_color: string | null
  tertiary_display_color: string | null
  display_shape: string
  display_order: number
  item_type: number
}
export interface ProjectJobType {
  id?: number
  job_type: JobType
  project: number
  is_active?: boolean
  manual_edits?: boolean
}
